import React, { useState, useEffect } from 'react';
import './Documentation.css';

interface DocSection {
  id: string;
  title: string;
  subsections?: { id: string; title: string }[];
  content: React.ReactNode;
}

const Documentation: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('overview');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['overview']));
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  const sections: DocSection[] = [
    {
      id: 'overview',
      title: 'Overview',
      subsections: [
        { id: 'what-is-lummo', title: 'What is Lummo?' },
        { id: 'key-features', title: 'Key Features' },
        { id: 'platform-components', title: 'Platform Components' }
      ],
      content: <OverviewSection />
    },
    {
      id: 'sdk',
      title: 'SDK Installation',
      subsections: [
        { id: 'prerequisites', title: 'Prerequisites' },
        { id: 'installation-steps', title: 'Installation Steps' },
        { id: 'configuration', title: 'Configuration' },
        { id: 'yaml-config', title: 'YAML Configuration' }
      ],
      content: <SDKSection />
    },
    {
      id: 'api-keys',
      title: 'API Keys & SaaS',
      subsections: [
        { id: 'supported-providers', title: 'Supported Providers' },
        { id: 'getting-api-key', title: 'Getting Your API Key' },
        { id: 'api-key-format', title: 'API Key Format' }
      ],
      content: <APIKeysSection />
    },
    {
      id: 'interoperability',
      title: 'Interoperability Systems',
      subsections: [
        { id: 'interoperability-overview', title: 'Overview' },
        { id: 'fhir-integration', title: 'FHIR Endpoint Integration' },
        { id: 'hl7v2-integration', title: 'HL7 v2 Integration' },
        { id: 'database-integration', title: 'Database Connection' },
        { id: 'supported-systems', title: 'Supported Systems' },
        { id: 'configuration-steps', title: 'Configuration Steps' }
      ],
      content: <InteroperabilitySection />
    },
    {
      id: 'agents',
      title: 'AI Agents',
      subsections: [
        { id: 'protocol-agent', title: 'Protocol Analysis Agent' },
        { id: 'site-readiness-agent', title: 'Site Readiness Agent' },
        { id: 'investigator-agent', title: 'Investigator Management Agent' },
        { id: 'patient-matching-agent', title: 'Patient Matching Agent' },
        { id: 'trial-feasibility-agent', title: 'Trial Feasibility Agent' },
        { id: 'agent-architecture', title: 'Agent Architecture' }
      ],
      content: <AgentsSection />
    },
    {
      id: 'architecture',
      title: 'Architecture',
      subsections: [
        { id: 'high-level-arch', title: 'High-Level Architecture' },
        { id: 'data-flow', title: 'Data Flow' },
        { id: 'security-arch', title: 'Security Architecture' }
      ],
      content: <ArchitectureSection />
    }
  ];

  const scrollToSubsection = (sectionId: string, subsectionId?: string) => {
    setActiveSection(sectionId);
    const targetId = subsectionId || sectionId;
    setTimeout(() => {
      const element = document.getElementById(targetId);
      if (element) {
        const offset = 20;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // Update active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const mainSections = ['overview', 'sdk', 'api-keys', 'interoperability', 'agents', 'architecture'];
      const scrollPosition = window.scrollY + 150;

      for (let i = mainSections.length - 1; i >= 0; i--) {
        const sectionId = mainSections[i];
        const element = document.getElementById(sectionId);
        if (element) {
          const sectionTop = element.offsetTop;
          if (scrollPosition >= sectionTop) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="documentation-page">
      {/* Mobile Menu Button */}
      <button 
        className="mobile-menu-btn"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? '✕' : '☰'}
      </button>
      
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      <div className="doc-container">
        {/* Sidebar Navigation */}
        <aside className={`doc-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="doc-sidebar-header">
            <h2>Lummo Docs</h2>
            <p className="doc-version">v1.0.0</p>
          </div>
          <nav className="doc-nav">
            {sections.map((section) => (
              <div key={section.id} className="doc-nav-group">
                <div className="doc-nav-item-wrapper">
                  <button
                    className={`doc-nav-item ${activeSection === section.id ? 'active' : ''}`}
                    onClick={() => {
                      scrollToSubsection(section.id);
                      if (!expandedSections.has(section.id) && section.subsections) {
                        setExpandedSections(prev => {
                          const newSet = new Set(Array.from(prev));
                          newSet.add(section.id);
                          return newSet;
                        });
                      }
                      // Close mobile menu when clicking a section
                      if (window.innerWidth <= 768) {
                        setMobileMenuOpen(false);
                      }
                    }}
                  >
                    <span>{section.title}</span>
                  </button>
                  {section.subsections && (
                    <button
                      className="nav-arrow-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedSections(prev => {
                          const newSet = new Set(Array.from(prev));
                          if (newSet.has(section.id)) {
                            newSet.delete(section.id);
                          } else {
                            newSet.add(section.id);
                          }
                          return newSet;
                        });
                      }}
                    >
                      {expandedSections.has(section.id) ? '▼' : '▶'}
                    </button>
                  )}
                </div>
                {section.subsections && expandedSections.has(section.id) && (
                  <div className="doc-nav-subsections">
                    {section.subsections.map((subsection) => (
                      <button
                        key={subsection.id}
                        className={`doc-nav-subitem ${activeSection === section.id ? 'active' : ''}`}
                        onClick={() => {
                          scrollToSubsection(section.id, subsection.id);
                          // Close mobile menu when clicking a subsection
                          if (window.innerWidth <= 768) {
                            setMobileMenuOpen(false);
                          }
                        }}
                      >
                        {subsection.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="doc-content">
          <div className="doc-content-inner">
            <div className="doc-header">
              <h1>Lummo Platform Documentation</h1>
              <p className="doc-subtitle">
                Complete guide to integrating and using Lummo's clinical trial management platform
              </p>
            </div>

            {sections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                className="doc-section"
              >
                {section.content}
              </section>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
};

// Overview Section
const OverviewSection: React.FC = () => (
  <div>
    <h2 id="what-is-lummo">What is Lummo?</h2>
    <p>
      Lummo is an AI Infrastructure platform purpose-built for clinical trials, designed to transform 
      how clinical research organizations (CROs), pharmaceutical companies, and clinical sites manage 
      the entire lifecycle of clinical trials. Our platform combines advanced artificial intelligence, 
      seamless data integration, and intelligent automation to solve critical challenges in patient 
      recruitment, protocol matching, site management, and regulatory compliance.
    </p>
    <p>
      At its core, Lummo provides a unified infrastructure layer that connects disparate healthcare 
      systems, from on-premise hospital EHRs to cloud-based SaaS platforms, and enables AI-powered 
      decision-making across the clinical trial ecosystem. We've built a microservices architecture 
      that scales horizontally, supports multiple deployment models (cloud, hybrid, on-premise), and 
      maintains strict compliance with healthcare data regulations.
    </p>
    <p>
      Our platform serves as the intelligent backbone for clinical trials, enabling organizations to:
    </p>
    <ul>
      <li>Automatically extract and structure inclusion/exclusion criteria from complex protocol documents using NLP</li>
      <li>Match patients to clinical trials in real-time using AI-powered algorithms that analyze clinical data</li>
      <li>Manage multi-site studies with granular control over investigators, coordinators, and regulatory workflows</li>
      <li>Integrate seamlessly with existing healthcare infrastructure without disrupting clinical operations</li>
      <li>Maintain complete audit trails and data provenance for regulatory compliance</li>
      <li>Scale from single-site studies to global multi-center trials with consistent performance</li>
    </ul>
    <p>
      Lummo's architecture is built on modern cloud-native principles, using Go microservices, 
      PostgreSQL for data persistence, and a flexible integration layer that supports both 
      traditional on-premise deployments and modern SaaS integrations. This design ensures 
      that healthcare organizations can adopt Lummo incrementally, starting with specific 
      use cases and expanding as their needs grow.
    </p>

    <h3 id="key-features">Key Features</h3>
    <ul className="feature-list">
      <li>
        <strong>AI-Powered Patient Matching:</strong> Advanced NLP and RAG (Retrieval-Augmented Generation) 
        algorithms automatically match patients to clinical trials based on structured and unstructured 
        inclusion/exclusion criteria. The system supports multi-language protocol analysis (Spanish, English, 
        Portuguese, French, Italian) and provides real-time matching scores with detailed evidence.
      </li>
      <li>
        <strong>Intelligent Protocol Analysis:</strong> Agentic extraction of clinical trial criteria using 
        optimized LLM prompts for maximum precision. The system intelligently detects relevant sections in 
        long documents (138+ pages), performs intelligent chunking, and extracts structured criteria with 
        validation capabilities.
      </li>
      <li>
        <strong>Site Management:</strong> Complete solution for managing clinical trial sites, investigators, 
        and study coordinators with role-based access control (CRO, Sponsor, Site). Includes modular settings 
        per organization, regulatory compliance tracking, and study lifecycle management.
      </li>
      <li>
        <strong>Dual Integration Model:</strong> Support for both on-premise Edge Agent deployments (via 
        secure WebSocket tunnels) and cloud-based SaaS integrations. The platform provides a unified 
        interface regardless of data source, enabling seamless transitions between deployment models.
      </li>
      <li>
        <strong>Enterprise SaaS Integration:</strong> Direct integration with major EHR systems including 
        Epic (FHIR R4), Cerner, Allscripts, athenahealth, and SaludTools. API keys are securely managed 
        via AWS Secrets Manager, and data is automatically transformed to Lummo Schema V1.
      </li>
      <li>
        <strong>Data Compliance & Provenance:</strong> Immutable audit trails for all data access, 
        complete data provenance tracking, and configurable data contracts (DCM) that define what data 
        can be accessed by which services. Supports pseudonymized data caching and regulatory reporting.
      </li>
    </ul>

    <h3 id="platform-components">Platform Components</h3>
    <p>
      Lummo is built as a microservices architecture, with each service handling a specific domain 
      of functionality. This design enables independent scaling, deployment, and maintenance while 
      maintaining clear boundaries and responsibilities.
    </p>
    <div className="component-grid">
      <div className="component-card">
        <h4>Core-MS</h4>
        <p>
          The central microservice managing the core business entities: protocols, studies, sites, 
          investigators, and regulatory compliance. Provides multi-user type support (CRO, Sponsor, Site) 
          with different permission levels, modular settings per organization, and seamless integration 
          with AI agents. Handles the complete lifecycle of clinical studies from protocol creation to 
          regulatory submission management.
        </p>
      </div>
      <div className="component-card">
        <h4>AI-Matching-MS</h4>
        <p>
          AI-powered service for protocol criteria extraction and patient matching. Uses advanced NLP 
          to extract inclusion/exclusion criteria from protocol documents (PDF/DOCX) via Apache Tika, 
          performs agentic extraction with optimized LLM prompts, and matches patients using structured 
          criteria comparison and RAG for unstructured criteria. Supports streaming via SSE for real-time 
          progress updates and persists criteria and candidates in PostgreSQL. Source-agnostic design 
          works identically with Edge Agent or SaaS data sources.
        </p>
      </div>
      <div className="component-card">
        <h4>Connect-Gateway-MS</h4>
        <p>
          Intelligent routing gateway that connects Lummo services to multiple data sources. Features 
          automatic detection of data source type (On-Premise vs SaaS), WebSocket tunnel management for 
          Edge Agents, SaaS integrators for major EHR systems, and centralized data transformation to 
          Lummo Schema V1. Implements Data Contract Management (DCM) validation, session management 
          with lease/heartbeat, and AWS Secrets Manager integration for secure API key storage.
        </p>
      </div>
      <div className="component-card">
        <h4>Data-MS</h4>
        <p>
          Data management and compliance service responsible for immutable audit trails (provenance logs), 
          pseudonymized data caching, Data Contract Management (DCM) metadata, and SaaS integration 
          configuration management. Ensures complete traceability of all data access, supports regulatory 
          reporting, and manages mapping rules for transforming external data formats to Lummo Schema V1.
        </p>
      </div>
      <div className="component-card">
        <h4>Sites-Connect-SDK (Edge Agent)</h4>
        <p>
          On-premise agent that connects local hospital systems (EHR/LIMS/PACS) with Lummo Cloud via 
          secure WebSocket tunnels. Configuration-driven approach where data access is guided by remote 
          MapperConfigs managed from the Lummo frontend. Establishes outbound secure connections (no 
          incoming ports required), maintains heartbeat/reconnection, executes queries against local 
          databases, and applies mapping rules to transform data to Lummo Schema V1. Supports multiple 
          database drivers and REST API integrations.
        </p>
      </div>
    </div>
  </div>
);

// SDK Installation Section
const SDKSection: React.FC = () => (
  <div>
    <h2 id="prerequisites">Prerequisites</h2>
    <ul>
      <li>Go 1.21 or higher</li>
      <li>PostgreSQL 14+ (for local data storage)</li>
      <li>Network access to Lummo Connect Gateway</li>
    </ul>

    <h2 id="installation-steps">Installation Steps</h2>
    <div className="code-block">
      <div className="code-header">
        <span>Terminal</span>
        <button className="copy-btn" onClick={() => navigator.clipboard.writeText('# Clone the repository\ngit clone https://github.com/lummo-io/sites-connect-sdk.git\ncd sites-connect-sdk\n\n# Build the edge agent\nmake build\n\n# Configure environment\ncp .env.example .env\n# Edit .env with your configuration\n\n# Run the edge agent\n./edge-agent')}>
          Copy
        </button>
      </div>
      <pre><code>{`# Clone the repository
git clone https://github.com/lummo-io/sites-connect-sdk.git
cd sites-connect-sdk

# Build the edge agent
make build

# Configure environment
cp .env.example .env
# Edit .env with your configuration

# Run the edge agent
./edge-agent`}</code></pre>
    </div>

    <h2 id="configuration">Configuration</h2>
    <p>
      <strong>Option 1: Via Lummo Platform (Recommended)</strong>
    </p>
    <p>
      The easiest way to configure your Edge Agent is through the Lummo platform interface:
    </p>
    <ol>
      <li>Log into your Lummo dashboard</li>
      <li>Navigate to <strong>Settings → Edge Agent Setup</strong></li>
      <li>Download the Edge Agent binary for your platform (Windows, Linux, macOS)</li>
      <li>Generate your <code>SITE_ID</code> and <code>EDGE_AGENT_KEY</code> from the platform</li>
      <li>Configure your data sources using the visual mapper interface</li>
      <li>Create MapperConfigs for each resource type (Patient, Observation, etc.)</li>
      <li>Define database connections, query templates, and mapping rules directly in the UI</li>
      <li>Publish and activate your configurations</li>
    </ol>
    <p>
      The platform automatically manages all configurations remotely, so you don't need to edit 
      files manually. The Edge Agent will pull the latest configurations on startup and when 
      changes are published.
    </p>
    
    <p>
      <strong>Option 2: Manual Configuration (Advanced)</strong>
    </p>
    <p>For advanced users, you can create a <code>.env</code> file with the following variables:</p>
    <div className="code-block">
      <div className="code-header">
        <span>.env</span>
        <button className="copy-btn" onClick={() => navigator.clipboard.writeText('LUMMO_GATEWAY_URL=https://gateway.lummo.io\nLUMMO_SITE_ID=your-site-id\nLUMMO_API_KEY=your-api-key\nDATABASE_URL=postgres://user:pass@localhost/lummo\nLOG_LEVEL=info')}>
          Copy
        </button>
      </div>
      <pre><code>{`LUMMO_GATEWAY_URL=https://gateway.lummo.io
LUMMO_SITE_ID=your-site-id
LUMMO_API_KEY=your-api-key
DATABASE_URL=postgres://user:pass@localhost/lummo
LOG_LEVEL=info`}</code></pre>
    </div>

    <h2 id="yaml-config">YAML Configuration (Advanced)</h2>
    <p>
      For advanced configurations, you can define data sources using a YAML file. The edge agent 
      supports multiple data source types including SQL databases, CSV files, and REST APIs. 
      <strong>Note:</strong> For most users, we recommend using the platform's visual mapper interface 
      in Settings, which automatically generates and manages these configurations.
    </p>
    <div className="code-block">
      <div className="code-header">
        <span>config.yaml</span>
        <button className="copy-btn" onClick={() => navigator.clipboard.writeText('data_sources:\n  - name: "EHR Database"\n    type: "postgresql"\n    connection_string: "postgres://..."\n    tables:\n      - patients\n      - diagnoses\n      - medications\n  - name: "Lab Results"\n    type: "rest_api"\n    endpoint: "https://lab-api.internal/api/v1"')}>
          Copy
        </button>
      </div>
      <pre><code>{`data_sources:
  - name: "EHR Database"
    type: "postgresql"
    connection_string: "postgres://..."
    tables:
      - patients
      - diagnoses
      - medications
  - name: "Lab Results"
    type: "rest_api"
    endpoint: "https://lab-api.internal/api/v1"`}</code></pre>
    </div>

    <div className="info-box">
      <strong>💡 Tip:</strong> The edge agent establishes a secure WebSocket tunnel to the 
      Connect Gateway, so you don't need to open any incoming ports in your firewall.
    </div>
  </div>
);

// API Keys Section
const APIKeysSection: React.FC = () => (
  <div>
    <h2 id="supported-providers">Supported Providers</h2>
    <p>
      For SaaS integrations, Lummo connects directly to cloud-based healthcare platforms using 
      their proprietary APIs with API keys and OAuth credentials. This eliminates the need for 
      on-premise infrastructure. These platforms have their own REST APIs that require specific 
      authentication methods (API Key/Secret).
    </p>
    <div className="info-box">
      <strong>💡 Note:</strong> If your system supports open standards like FHIR or HL7 v2, 
      see the <strong>Interoperability Systems</strong> section below for connection instructions.
    </div>

    <div className="provider-grid">
      <div className="provider-card">
        <h4>Epic</h4>
        <p>FHIR R4 API integration</p>
        <ul>
          <li>OAuth 2.0 authentication</li>
          <li>Patient demographics</li>
          <li>Clinical notes</li>
          <li>Lab results</li>
        </ul>
      </div>
      <div className="provider-card">
        <h4>Cerner</h4>
        <p>SMART on FHIR integration</p>
        <ul>
          <li>SMART app registration</li>
          <li>Patient data access</li>
          <li>Clinical decision support</li>
        </ul>
      </div>
      <div className="provider-card">
        <h4>SaludTools</h4>
        <p>Comprehensive LATAM platform</p>
        <ul>
          <li>Electronic Health Records</li>
          <li>Patient management</li>
          <li>Laboratory integration</li>
        </ul>
      </div>
      <div className="provider-card">
        <h4>Allscripts</h4>
        <p>EHR and practice management</p>
        <ul>
          <li>API access credentials</li>
          <li>Patient engagement</li>
          <li>Clinical documentation</li>
        </ul>
      </div>
      <div className="provider-card">
        <h4>athenahealth</h4>
        <p>Cloud-based health platform</p>
        <ul>
          <li>OAuth authentication</li>
          <li>Patient records</li>
          <li>Practice management</li>
        </ul>
      </div>
    </div>

    <h2 id="getting-api-key">Getting Your API Key</h2>
    <p>
      All SaaS integrations are configured directly from the Lummo platform interface. You don't 
      need to manage API keys manually or write any code.
    </p>
    <ol>
      <li>Log into your Lummo dashboard</li>
      <li>Navigate to <strong>Settings → Data Sources</strong></li>
      <li>In the <strong>SaaS Platforms</strong> section, select your provider from the list</li>
      <li>Enter your API Key and API Secret (if required) in the secure form</li>
      <li>Click <strong>Test Connection</strong> to verify your credentials</li>
      <li>Click <strong>Save Configuration</strong> to activate the integration</li>
    </ol>
    <p>
      Your API credentials are automatically encrypted and stored in AWS Secrets Manager. They 
      are never exposed in logs, error messages, or API responses. Each integration is scoped to 
      a specific site and company, ensuring complete isolation.
    </p>

    <h2 id="api-key-format">API Key Format</h2>
    <p>Lummo API keys follow this format:</p>
    <div className="code-block">
      <pre><code>{`lummo_live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`}</code></pre>
    </div>

    <div className="warning-box">
      <strong>⚠️ Security Note:</strong> API keys are stored encrypted in AWS Secrets Manager 
      and never exposed in logs or error messages. Each key is scoped to a specific site 
      and company.
    </div>
  </div>
);

// Interoperability Systems Section
const InteroperabilitySection: React.FC = () => (
  <div>
    <h2 id="interoperability-overview">Overview</h2>
    <p>
      For hospitals using on-premise or legacy systems that support open interoperability standards, 
      Lummo provides multiple connection methods: <strong>FHIR endpoints</strong>, <strong>HL7 v2 messaging</strong>, 
      and <strong>direct database connections</strong>. This section covers systems like Servinte, 
      Dinámica Gerencial SYAC, CLTech, Athenea, and other EHR/LIS systems that support these standards.
    </p>
    <p>
      Unlike SaaS platforms that use proprietary APIs, interoperability systems connect via industry-standard 
      protocols that are vendor-agnostic. This allows Lummo to integrate with a wide range of healthcare systems 
      without requiring custom adapters for each one.
    </p>

    <div className="info-box">
      <strong>🔗 Key Difference:</strong> SaaS Platforms (like SaludTools, Epic) use proprietary REST APIs 
      with API Key/Secret authentication. Interoperability Systems use open standards (FHIR, HL7 v2) or 
      direct database access, making them accessible to any system that supports these standards.
    </div>

    <h2 id="fhir-integration">FHIR Endpoint Integration</h2>
    <p>
      If your EHR system exposes a <strong>FHIR R4 endpoint</strong>, you can connect directly without 
      installing Edge Agent. FHIR (Fast Healthcare Interoperability Resources) is the modern standard for 
      healthcare data exchange.
    </p>

    <h3>Prerequisites</h3>
    <ul>
      <li>Your EHR system must expose a FHIR R4 Base URL (e.g., <code>https://fhir.yourhospital.com/fhir</code>)</li>
      <li>Authentication method: OAuth 2.0, API Key, or Certificate-based</li>
      <li>Network access: The FHIR endpoint must be accessible from Lummo's cloud infrastructure</li>
      <li>Required FHIR resources: Patient, Observation, Condition, MedicationRequest (at minimum)</li>
    </ul>

    <h3>Configuration Steps</h3>
    <ol>
      <li>
        <strong>Obtain FHIR Endpoint URL</strong>
        <p>Contact your EHR vendor or IT department to get your FHIR Base URL. It typically looks like:</p>
        <div className="code-block">
          <pre><code>{`https://fhir.yourhospital.com/fhir
https://api.your-ehr-system.com/fhir/r4
https://yourhospital.com/hapi/fhir`}</code></pre>
        </div>
      </li>
      <li>
        <strong>Get Authentication Credentials</strong>
        <p>Depending on your system's authentication method:</p>
        <ul>
          <li><strong>OAuth 2.0:</strong> Client ID and Client Secret from your EHR vendor</li>
          <li><strong>API Key:</strong> API key provided by your system administrator</li>
          <li><strong>Certificate:</strong> Client certificate (.pem or .p12 file) for mutual TLS</li>
        </ul>
      </li>
      <li>
        <strong>Configure in Lummo Platform</strong>
        <ol>
          <li>Log into your Lummo dashboard</li>
          <li>Navigate to <strong>Settings → Data Sources</strong></li>
          <li>In the <strong>Interoperability Systems</strong> section, find the <strong>Electronic Health Records (EHR)</strong> card</li>
          <li>Enter your system name (e.g., "Servinte", "Dinámica SYAC")</li>
          <li>Select <strong>Connection Type: FHIR Endpoint</strong></li>
          <li>Enter your FHIR Base URL in the <strong>Endpoint / Connection</strong> field</li>
          <li>Select your <strong>Authentication Method</strong> (OAuth 2.0, API Key, or Certificate)</li>
          <li>Enter authentication credentials as required</li>
          <li>Click <strong>Connect</strong> to test and save the configuration</li>
        </ol>
      </li>
    </ol>

    <h3>FHIR Resources Supported</h3>
    <p>Lummo automatically queries and processes the following FHIR resources:</p>
    <div className="code-block">
      <pre><code>{`Patient              - Demographics and basic patient information
Condition            - Diagnoses and medical conditions
Observation          - Lab results, vital signs, clinical measurements
MedicationRequest    - Current and past medications
Encounter            - Hospital visits and encounters
Procedure            - Medical procedures performed
DiagnosticReport     - Diagnostic test results
AllergyIntolerance   - Patient allergies
Immunization         - Vaccination records`}</code></pre>
    </div>

    <h3>Example FHIR Endpoint Configuration</h3>
    <div className="code-block">
      <div className="code-header">
        <span>Configuration Example</span>
      </div>
      <pre><code>{`System Name: Servinte
Connection Type: FHIR Endpoint
Endpoint: https://fhir.servinte.com/fhir/r4
Authentication: OAuth 2.0
Client ID: your-client-id
Client Secret: your-client-secret`}</code></pre>
    </div>

    <h2 id="hl7v2-integration">HL7 v2 Integration</h2>
    <p>
      For legacy systems that use <strong>HL7 v2 messaging</strong> (common in Latin America), Lummo supports 
      receiving HL7 v2 messages via MLLP (Minimal Lower Layer Protocol) or HTTP endpoints. HL7 v2 is the 
      traditional messaging standard for healthcare data exchange.
    </p>

    <h3>Supported HL7 v2 Message Types</h3>
    <ul>
      <li><strong>ADT (Admit, Discharge, Transfer):</strong> Patient demographics and admission/discharge events</li>
      <li><strong>ORU (Observation Result):</strong> Laboratory results and clinical observations</li>
      <li><strong>ORM (Order Message):</strong> Medication orders and prescriptions</li>
    </ul>

    <h3>Connection Methods</h3>
    
    <h4>Method 1: MLLP (Minimal Lower Layer Protocol) - Recommended</h4>
    <p>
      MLLP is the standard protocol for HL7 v2 over TCP/IP. Your system sends HL7 v2 messages to Lummo's 
      MLLP endpoint.
    </p>
    <div className="code-block">
      <div className="code-header">
        <span>MLLP Configuration</span>
      </div>
      <pre><code>{`Endpoint: mllp://gateway.lummo.io:2575
Protocol: MLLP over TCP/IP
Port: 2575 (default, configurable)
Authentication: Site ID and Edge Agent Key`}</code></pre>
    </div>
    <p>
      <strong>Configuration Steps:</strong>
    </p>
    <ol>
      <li>Configure your HL7 v2 interface engine (e.g., Mirth Connect, Rhapsody, Cloverleaf) to send messages to Lummo's MLLP endpoint</li>
      <li>In Lummo: Navigate to <strong>Settings → Data Sources → Interoperability Systems</strong></li>
      <li>Select your system type (EHR, LIS, or Imaging)</li>
      <li>Choose <strong>Connection Type: HL7 v2</strong></li>
      <li>Enter your system name and connection details</li>
      <li>Lummo will provide you with the MLLP endpoint URL and authentication credentials</li>
    </ol>

    <h4>Method 2: HTTP Endpoint (Alternative)</h4>
    <p>
      If your system cannot use MLLP, Lummo also accepts HL7 v2 messages via HTTP POST.
    </p>
    <div className="code-block">
      <div className="code-header">
        <span>HTTP Endpoint</span>
        <button className="copy-btn" onClick={() => navigator.clipboard.writeText('POST https://gateway.lummo.io/api/v1/hl7v2/message\nContent-Type: text/plain\nAuthorization: Bearer <your-token>\n\nMSH|^~\\&|SendingApp|SendingFacility|ReceivingApp|ReceivingFacility|20250112120000||ADT^A01|12345|P|2.5\rPID|1||123456789^^^MRN||DOE^JOHN^MIDDLE||19900101|M|||123 MAIN ST^^CITY^ST^12345||555-1234|||S')}>
          Copy
        </button>
      </div>
      <pre><code>{`POST https://gateway.lummo.io/api/v1/hl7v2/message
Content-Type: text/plain
Authorization: Bearer <your-token>

MSH|^~\\&|SendingApp|SendingFacility|ReceivingApp|ReceivingFacility|20250112120000||ADT^A01|12345|P|2.5
PID|1||123456789^^^MRN||DOE^JOHN^MIDDLE||19900101|M|||123 MAIN ST^^CITY^ST^12345||555-1234|||S`}</code></pre>
    </div>

    <h3>HL7 v2 to FHIR Conversion</h3>
    <p>
      Lummo automatically converts incoming HL7 v2 messages to FHIR resources, ensuring compatibility 
      with the rest of the platform. The conversion handles:
    </p>
    <ul>
      <li><strong>ADT Messages →</strong> Patient and Encounter resources</li>
      <li><strong>ORU Messages →</strong> Observation resources (lab results, vital signs)</li>
      <li><strong>ORM Messages →</strong> MedicationRequest resources</li>
      <li><strong>AL1 Segments →</strong> AllergyIntolerance resources</li>
      <li><strong>DG1 Segments →</strong> Condition resources (diagnoses)</li>
    </ul>

    <h3>Example: Servinte HL7 v2 Configuration</h3>
    <div className="code-block">
      <div className="code-header">
        <span>Servinte Configuration</span>
      </div>
      <pre><code>{`System Name: Servinte
Connection Type: HL7 v2
Endpoint: mllp://gateway.lummo.io:2575
Message Types: ADT^A01, ADT^A08, ORU^R01
Authentication: Site ID + Edge Agent Key`}</code></pre>
    </div>

    <h2 id="database-integration">Database Connection</h2>
    <p>
      For systems that don't expose FHIR or HL7 v2 interfaces, you can connect directly to the underlying 
      database (PostgreSQL, MySQL, SQL Server, Oracle). This is common for legacy systems or when you have 
      direct database access.
    </p>

    <h3>Prerequisites</h3>
    <ul>
      <li>Database connection string (host, port, database name, credentials)</li>
      <li>Read-only database user account (recommended for security)</li>
      <li>Network access: Database must be accessible from Lummo's infrastructure OR use Edge Agent for on-premise databases</li>
      <li>Knowledge of database schema (table names, column names)</li>
    </ul>

    <h3>Configuration Steps</h3>
    <ol>
      <li>
        <strong>Create Read-Only Database User</strong>
        <p>For security, create a dedicated read-only user:</p>
        <div className="code-block">
          <div className="code-header">
            <span>PostgreSQL Example</span>
            <button className="copy-btn" onClick={() => navigator.clipboard.writeText('CREATE USER lummo_readonly WITH PASSWORD \'secure-password\';\nGRANT CONNECT ON DATABASE your_database TO lummo_readonly;\nGRANT USAGE ON SCHEMA public TO lummo_readonly;\nGRANT SELECT ON ALL TABLES IN SCHEMA public TO lummo_readonly;')}>
              Copy
            </button>
          </div>
          <pre><code>{`CREATE USER lummo_readonly WITH PASSWORD 'secure-password';
GRANT CONNECT ON DATABASE your_database TO lummo_readonly;
GRANT USAGE ON SCHEMA public TO lummo_readonly;
GRANT SELECT ON ALL TABLES IN SCHEMA public TO lummo_readonly;`}</code></pre>
        </div>
      </li>
      <li>
        <strong>Configure in Lummo Platform</strong>
        <ol>
          <li>Navigate to <strong>Settings → Data Sources → Interoperability Systems</strong></li>
          <li>Select the appropriate system type (EHR, LIS, or Imaging)</li>
          <li>Enter your system name (e.g., "CLTech Database", "Athenea LIS")</li>
          <li>Select <strong>Connection Type: Database Connection</strong></li>
          <li>Enter your database connection string:
            <div className="code-block">
              <pre><code>{`PostgreSQL: postgresql://user:password@host:5432/database
MySQL: mysql://user:password@host:3306/database
SQL Server: sqlserver://user:password@host:1433/database
Oracle: oracle://user:password@host:1521/SID`}</code></pre>
            </div>
          </li>
          <li>Select your <strong>Database Type</strong></li>
          <li>Click <strong>Connect</strong> to test the connection</li>
        </ol>
      </li>
      <li>
        <strong>Configure Data Mapping (via Edge Agent or Platform UI)</strong>
        <p>
          After connecting, you'll need to map your database tables/columns to Lummo Schema V1. 
          This can be done via the Lummo platform's visual mapper interface or through Edge Agent 
          configuration if the database is on-premise.
        </p>
      </li>
    </ol>

    <h3>On-Premise Database Access</h3>
    <p>
      If your database is on-premise and not accessible from the internet, you have two options:
    </p>
    <ul>
      <li>
        <strong>Option 1: Edge Agent (Recommended)</strong>
        <p>
          Install Lummo Edge Agent on a server within your network that has access to the database. 
          The Edge Agent will query the database locally and forward data to Lummo via secure WebSocket tunnel.
        </p>
      </li>
      <li>
        <strong>Option 2: VPN Tunnel</strong>
        <p>
          Establish a VPN connection between your network and Lummo's infrastructure, allowing 
          direct database access. This requires network configuration on both sides.
        </p>
      </li>
    </ul>

    <h2 id="supported-systems">Supported Systems</h2>
    <p>
      Lummo supports interoperability with the following systems commonly used in Latin America and globally:
    </p>

    <div className="provider-grid">
      <div className="provider-card">
        <h4>Servinte</h4>
        <p>Colombian EHR System</p>
        <ul>
          <li>Connection: FHIR Endpoint or HL7 v2</li>
          <li>Resources: Patient, Clinical History, Lab Results</li>
          <li>Authentication: OAuth 2.0 or API Key</li>
        </ul>
      </div>
      <div className="provider-card">
        <h4>Dinámica Gerencial SYAC</h4>
        <p>Colombian Healthcare Management</p>
        <ul>
          <li>Connection: HL7 v2 or Database</li>
          <li>Resources: EHR, Imaging (RIS/PACS)</li>
          <li>Authentication: MLLP or Database credentials</li>
        </ul>
      </div>
      <div className="provider-card">
        <h4>CLTech</h4>
        <p>Colombian Laboratory Information System</p>
        <ul>
          <li>Connection: HL7 v2 (ORU messages)</li>
          <li>Resources: Lab Results, Test Orders</li>
          <li>Authentication: MLLP endpoint</li>
        </ul>
      </div>
      <div className="provider-card">
        <h4>Athenea</h4>
        <p>Laboratory Information System</p>
        <ul>
          <li>Connection: HL7 v2 or Database</li>
          <li>Resources: Laboratory Results</li>
          <li>Authentication: MLLP or Database credentials</li>
        </ul>
      </div>
    </div>

    <div className="info-box">
      <strong>💡 Don't see your system?</strong> If your system supports FHIR R4, HL7 v2, or provides 
      database access, it can be integrated. Contact Lummo support for assistance with your specific system.
    </div>

    <h2 id="configuration-steps">Complete Configuration Guide</h2>
    <p>
      Follow these steps to configure your interoperability system connection:
    </p>

    <h3>Step 1: Determine Your Connection Type</h3>
    <p>Identify which method your system supports:</p>
    <ul>
      <li><strong>FHIR Endpoint:</strong> Your system exposes a FHIR R4 Base URL</li>
      <li><strong>HL7 v2:</strong> Your system can send HL7 v2 messages (ADT, ORU, ORM)</li>
      <li><strong>Database:</strong> You have direct database access (PostgreSQL, MySQL, SQL Server, Oracle)</li>
    </ul>

    <h3>Step 2: Gather Required Information</h3>
    <div className="code-block">
      <div className="code-header">
        <span>Information Checklist</span>
      </div>
      <pre><code>{`For FHIR:
  □ FHIR Base URL
  □ Authentication method (OAuth 2.0 / API Key / Certificate)
  □ Client ID and Secret (if OAuth)
  □ API Key (if API Key auth)
  □ Certificate file (if Certificate auth)

For HL7 v2:
  □ HL7 v2 interface engine (Mirth, Rhapsody, etc.)
  □ Message types your system sends (ADT, ORU, ORM)
  □ MLLP endpoint capability
  □ Network access to Lummo's MLLP endpoint

For Database:
  □ Database type (PostgreSQL, MySQL, SQL Server, Oracle)
  □ Connection string (host, port, database name)
  □ Database user credentials (read-only recommended)
  □ Table/column names for patient data
  □ Network access (or Edge Agent for on-premise)`}</code></pre>
    </div>

    <h3>Step 3: Configure in Lummo Platform</h3>
    <ol>
      <li>Log into your Lummo dashboard</li>
      <li>Navigate to <strong>Settings → Data Sources</strong></li>
      <li>Scroll to the <strong>Interoperability Systems</strong> section</li>
      <li>Select the appropriate card:
        <ul>
          <li><strong>Electronic Health Records (EHR):</strong> For Servinte, Dinámica SYAC, etc.</li>
          <li><strong>Laboratory Information System (LIS):</strong> For CLTech, Athenea, etc.</li>
          <li><strong>Imaging System (RIS/PACS):</strong> For imaging/radiology systems</li>
        </ul>
      </li>
      <li>Fill in the configuration form:
        <ul>
          <li><strong>System Name:</strong> Enter your system name</li>
          <li><strong>Connection Type:</strong> Select FHIR Endpoint, HL7 v2, or Database Connection</li>
          <li><strong>Endpoint / Connection:</strong> Enter your FHIR URL, MLLP endpoint, or database connection string</li>
          <li><strong>Authentication Method:</strong> Select and configure as required</li>
        </ul>
      </li>
      <li>Click <strong>Connect</strong> to test the connection</li>
      <li>If successful, the system will be saved and activated</li>
    </ol>

    <h3>Step 4: Verify Data Flow</h3>
    <p>
      After configuration, verify that data is flowing correctly:
    </p>
    <ul>
      <li>Check the connection status in the Lummo dashboard (should show "Connected")</li>
      <li>Test patient data retrieval through the platform</li>
      <li>Verify that patient demographics, lab results, and clinical data are being received</li>
      <li>Review any error logs in the Lummo dashboard if issues occur</li>
    </ul>

    <h3>Troubleshooting</h3>
    <div className="warning-box">
      <strong>Common Issues:</strong>
      <ul>
        <li><strong>Connection Timeout:</strong> Check network firewall rules and ensure Lummo's IPs are whitelisted</li>
        <li><strong>Authentication Failed:</strong> Verify credentials are correct and not expired</li>
        <li><strong>No Data Received:</strong> Check that your system is actually sending data and that message formats are correct</li>
        <li><strong>FHIR Resources Missing:</strong> Ensure your FHIR endpoint supports the required resources (Patient, Observation, etc.)</li>
      </ul>
    </div>

    <div className="info-box">
      <strong>📞 Need Help?</strong> If you encounter issues during configuration, contact Lummo support 
      with your system name, connection type, and any error messages. We can assist with system-specific 
      configuration details.
    </div>
  </div>
);

// Agents Section
const AgentsSection: React.FC = () => (
  <div>
    <h2>AI Agents</h2>
    <p>
      Lummo uses AI agents powered by large language models to automate and enhance 
      various aspects of clinical trial management. Each agent is specialized for specific tasks.
    </p>

    <h3 id="protocol-agent">Protocol Analysis Agent</h3>
    <div className="agent-card">
      <div className="agent-header">
        <h4>Protocol Analysis Agent</h4>
        <span className="agent-status active">Active</span>
      </div>
      <p>
        Analyzes clinical trial protocols to extract inclusion/exclusion criteria, 
        endpoints, and study design information.
      </p>
      <h5>Capabilities:</h5>
      <ul>
        <li>Natural language protocol parsing</li>
        <li>Criteria extraction and structuring</li>
        <li>Protocol comparison and matching</li>
        <li>Regulatory compliance checking</li>
      </ul>
      <div className="code-block">
        <div className="code-header">
          <span>Example Usage</span>
        </div>
        <pre><code>{`POST /api/agent-ai/protocol-copilot/query
{
  "query": "Extract inclusion criteria from this protocol",
  "protocol_id": "protocol-123"
}`}</code></pre>
      </div>
    </div>

    <h3 id="site-readiness-agent">Site Readiness Agent</h3>
    <div className="agent-card">
      <div className="agent-header">
        <h4>Site Readiness Agent</h4>
        <span className="agent-status active">Active</span>
      </div>
      <p>
        Evaluates clinical trial sites to assess their readiness for specific protocols, 
        considering infrastructure, staff, and patient population.
      </p>
      <h5>Capabilities:</h5>
      <ul>
        <li>Site capability assessment</li>
        <li>Infrastructure evaluation</li>
        <li>Staff qualification matching</li>
        <li>Patient population analysis</li>
      </ul>
    </div>

    <h3 id="investigator-agent">Investigator Management Agent</h3>
    <div className="agent-card">
      <div className="agent-header">
        <h4>Investigator Management Agent</h4>
        <span className="agent-status active">Active</span>
      </div>
      <p>
        Manages and analyzes clinical investigators, their qualifications, availability, 
        and performance metrics.
      </p>
      <h5>Capabilities:</h5>
      <ul>
        <li>Investigator profile analysis</li>
        <li>Availability tracking</li>
        <li>Performance metrics calculation</li>
        <li>Specialty matching</li>
        <li>CRUD operations via natural language</li>
      </ul>
      <div className="code-block">
        <div className="code-header">
          <span>Example Tools</span>
        </div>
        <pre><code>{`- create_investigator(name, email, specialty, data)
- get_investigator_stats(site_id)
- find_matching_investigators(criteria)
- update_investigator_availability(id, status)`}</code></pre>
      </div>
    </div>

    <h3 id="patient-matching-agent">Patient Matching Agent</h3>
    <div className="agent-card">
      <div className="agent-header">
        <h4>Patient Matching Agent</h4>
        <span className="agent-status development">In Development</span>
      </div>
      <p>
        <strong>Upcoming Feature:</strong> Advanced AI agent for matching patients to clinical 
        trials based on complex inclusion/exclusion criteria, medical history, and real-time 
        health data.
      </p>
      <h5>Planned Capabilities:</h5>
      <ul>
        <li>
          <strong>Multi-criteria Matching:</strong> Analyzes patient demographics, medical history, 
          lab results, medications, and comorbidities against protocol criteria
        </li>
        <li>
          <strong>Real-time Data Processing:</strong> Continuously monitors patient data for 
          eligibility changes
        </li>
        <li>
          <strong>Confidence Scoring:</strong> Provides match confidence scores with explanations
        </li>
        <li>
          <strong>Privacy-Preserving:</strong> Works with both edge agents (on-premise) and 
          SaaS integrations (encrypted)
        </li>
        <li>
          <strong>Fuzzy Matching:</strong> Handles variations in medical terminology and 
          coding systems (ICD-10, SNOMED CT, etc.)
        </li>
        <li>
          <strong>Longitudinal Analysis:</strong> Considers patient history over time, not just 
          current state
        </li>
      </ul>
      <h5>Technical Approach:</h5>
      <p>
        The Patient Matching Agent will use a combination of:
      </p>
      <ul>
        <li>
          <strong>NLP Models:</strong> For understanding unstructured clinical notes and 
          protocol descriptions
        </li>
        <li>
          <strong>Rule-based Engine:</strong> For precise criteria matching (age, lab values, etc.)
        </li>
        <li>
          <strong>Machine Learning:</strong> For learning from historical matching decisions 
          and improving accuracy
        </li>
        <li>
          <strong>Graph Database:</strong> For modeling complex relationships between conditions, 
          medications, and protocols
        </li>
      </ul>
      <div className="info-box">
        <strong>🚀 Roadmap:</strong> Expected release Q2 2025. The agent will integrate with 
        our existing AI-Matching-MS infrastructure and support both real-time and batch 
        matching workflows.
      </div>
    </div>

    <h3 id="trial-feasibility-agent">Trial Feasibility Agent</h3>
    <div className="agent-card">
      <div className="agent-header">
        <h4>Trial Feasibility Agent</h4>
        <span className="agent-status active">Active</span>
      </div>
      <p>
        Assesses the feasibility of conducting a clinical trial at specific sites based on 
        patient population, infrastructure, and regulatory considerations.
      </p>
      <h5>Capabilities:</h5>
      <ul>
        <li>Patient population analysis</li>
        <li>Recruitment timeline estimation</li>
        <li>Resource requirement assessment</li>
        <li>Regulatory compliance evaluation</li>
      </ul>
    </div>

    <h3 id="agent-architecture">Agent Architecture</h3>
    <p>
      All agents are built using the AI SDK pattern with:
    </p>
    <ul>
      <li><strong>Tool-based Execution:</strong> Agents use tools to interact with the platform</li>
      <li><strong>Context Management:</strong> Maintains conversation history and relevant context</li>
      <li><strong>Loop Control:</strong> Automatic stopping conditions and step limits</li>
      <li><strong>Streaming Support:</strong> Real-time response streaming for better UX</li>
    </ul>
  </div>
);

// Architecture Section
const ArchitectureSection: React.FC = () => {
  useEffect(() => {
    // Load Mermaid dynamically
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js';
    script.async = true;
    
    const initializeMermaid = () => {
      if ((window as any).mermaid) {
        (window as any).mermaid.initialize({ 
          startOnLoad: true, 
          theme: 'default',
          securityLevel: 'loose'
        });
        (window as any).mermaid.run();
      }
    };

    script.onload = initializeMermaid;
    
    // Check if already loaded
    if ((window as any).mermaid) {
      initializeMermaid();
    } else {
      document.head.appendChild(script);
    }

    return () => {
      // Cleanup if needed
    };
  }, []);

  return (
    <div>
      <h2 id="high-level-arch">High-Level Architecture</h2>
      <p>
        Lummo is built on a microservices architecture, ensuring scalability, reliability, 
        and maintainability.
      </p>

      <div className="mermaid-diagram">
        <div className="mermaid">
{`graph TB
    subgraph "Frontend Layer"
        A["Sites Admin<br/>React/TypeScript"]
        B["Lummo Platform<br/>Next.js"]
        C["Public Website<br/>React"]
    end
    
    subgraph "API Gateway Layer"
        D["Connect Gateway MS"]
        E["Authentication & Authorization"]
    end
    
    subgraph "Core Services"
        F["Core-MS<br/>Protocols, Sites, Investigators"]
        G["AI-Matching-MS<br/>Patient Matching"]
        H["Data-MS<br/>Compliance & Audit"]
        I["AI Agent Service<br/>Python/LangChain"]
    end
    
    subgraph "Data Layer"
        J[("PostgreSQL<br/>Primary Database")]
        K["AWS Secrets Manager<br/>Credentials"]
        L["S3<br/>Document Storage"]
    end
    
    subgraph "Integration Layer"
        M["Edge Agents<br/>On-Premise"]
        N["SaaS Integrations<br/>Epic, Cerner, etc."]
    end
    
    A --> D
    B --> D
    C --> D
    D --> E
    E --> F
    E --> G
    E --> H
    E --> I
    F --> J
    G --> J
    H --> J
    F --> K
    G --> L
    D --> M
    D --> N
    M -.->|"WebSocket Tunnel"| D
    N -.->|"API/HTTP"| D`}
        </div>
      </div>

      <h2 id="data-flow">Data Flow</h2>
      <ol>
        <li>User interacts with frontend application</li>
        <li>Request authenticated via Auth0/Descope</li>
        <li>API Gateway routes to appropriate microservice</li>
        <li>Service processes request and queries database</li>
        <li>For patient matching: AI-Matching-MS processes criteria</li>
        <li>Results returned through API Gateway to frontend</li>
      </ol>

      <h2 id="security-arch">Security Architecture</h2>
      <p>
        Security and data privacy are fundamental to Lummo's architecture. We've designed the platform 
        with a zero-trust security model, ensuring that sensitive clinical data never leaves your control 
        without explicit authorization and proper safeguards.
      </p>

      <h3>Data Protection & Privacy</h3>
      <ul>
        <li>
          <strong>No Data Transmission Over Public Internet:</strong> All data communication between 
          Edge Agents and Lummo Cloud uses secure WebSocket tunnels with end-to-end encryption. For 
          on-premise deployments, data never leaves your network infrastructure. The Edge Agent establishes 
          outbound connections only (no incoming ports required), ensuring your firewall remains closed.
        </li>
        <li>
          <strong>Self-Hosted AI Infrastructure:</strong> Lummo uses self-hosted AI models running on 
          your infrastructure or our private cloud. We do not use public AI APIs (OpenAI, Anthropic, etc.) 
          for processing clinical data. All AI operations, including protocol analysis, patient matching, 
          and criteria extraction, run on dedicated, isolated infrastructure with no data sharing with 
          third-party AI providers.
        </li>
        <li>
          <strong>Data Residency & Sovereignty:</strong> You maintain complete control over where your 
          data is stored and processed. Edge Agent deployments keep all data on-premise, with only 
          aggregated, anonymized results transmitted to Lummo Cloud when explicitly configured.
        </li>
        <li>
          <strong>Pseudonymization & Anonymization:</strong> All patient data is pseudonymized before 
          processing. The platform supports configurable anonymization rules, ensuring compliance with 
          HIPAA, GDPR, and other regulatory requirements.
        </li>
        <li>
          <strong>Immutable Audit Trails:</strong> Every data access, modification, and AI operation 
          is logged in an immutable provenance system. These logs cannot be altered or deleted, providing 
          complete traceability for regulatory audits.
        </li>
      </ul>

      <h3>Authentication & Authorization</h3>
      <ul>
        <li>
          <strong>OAuth 2.0 / JWT Tokens:</strong> All API access requires valid authentication tokens. 
          Tokens are short-lived and automatically rotated, reducing the risk of unauthorized access.
        </li>
        <li>
          <strong>Role-Based Access Control (RBAC):</strong> Granular permissions based on user roles 
          (CRO, Sponsor, Site). Each role has specific access rights, ensuring users can only access 
          data and perform actions appropriate to their role.
        </li>
        <li>
          <strong>Data Contract Management (DCM):</strong> Fine-grained data access control through 
          configurable contracts. Each site defines what data can be accessed, by which services, and 
          for what purposes. The Connect Gateway validates every request against these contracts.
        </li>
      </ul>

      <h3>Infrastructure Security</h3>
      <ul>
        <li>
          <strong>Encryption in Transit:</strong> All network communication uses TLS 1.3 with perfect 
          forward secrecy. WebSocket tunnels are encrypted end-to-end.
        </li>
        <li>
          <strong>Encryption at Rest:</strong> All databases use encryption at rest. Sensitive fields 
          are additionally encrypted using application-level encryption.
        </li>
        <li>
          <strong>API Key Management:</strong> All API keys and credentials are stored in AWS Secrets 
          Manager with automatic rotation. Keys are never exposed in logs, error messages, or API responses.
        </li>
        <li>
          <strong>Secure Edge Agent Communication:</strong> Edge Agents use secure WebSocket tunnels with 
          mutual TLS authentication. Each agent is issued a unique lease token that must be validated 
          for every connection. No incoming ports are required on your firewall.
        </li>
        <li>
          <strong>Network Isolation:</strong> Microservices communicate over private networks. Public 
          access is only available through the API Gateway, which implements rate limiting, DDoS protection, 
          and request validation.
        </li>
      </ul>

      <h3>Compliance & Regulatory</h3>
      <ul>
        <li>
          <strong>HIPAA Compliance:</strong> Platform designed to meet HIPAA requirements for protected 
          health information (PHI). Includes Business Associate Agreement (BAA) support and complete audit 
          trail capabilities.
        </li>
        <li>
          <strong>GDPR Compliance:</strong> Supports data subject rights including right to access, 
          right to erasure, and data portability. Pseudonymization and anonymization features ensure 
          compliance with GDPR requirements.
        </li>
        <li>
          <strong>21 CFR Part 11:</strong> Immutable audit trails and electronic signature capabilities 
          support FDA 21 CFR Part 11 compliance for clinical trial data.
        </li>
        <li>
          <strong>Regular Security Audits:</strong> Platform undergoes regular security assessments and 
          penetration testing. All vulnerabilities are addressed through a structured vulnerability 
          management program.
        </li>
      </ul>

      <div className="info-box">
        <strong>🔒 Security Commitment:</strong> We understand that clinical trial data is among the 
        most sensitive information in healthcare. Lummo is built with security as a first-class concern, 
        not an afterthought. Your data never leaves your control without explicit authorization, and we 
        never use public AI APIs that could expose your data to third parties.
      </div>
    </div>
  );
};

export default Documentation;
